import '../../../assets/css/website/guides.css';

export default function ShopifyOnboarding() {

  return (
    <div className="guide">
      <h1 className="guide__h1">Shopify Onboarding</h1>
      <div className="guide__intro">
        <h2 className="guide__h2">What you will learn</h2>
        <ol>How to set up your Loyalty Program on your Shopify store
          <ul>
            <li>Edit your theme file</li>
            <li>Create dedicated Loyalty page</li>
            <li>Add additional Shopify sections</li>
          </ul>
        </ol>
      </div>

      <h3 className="guide__h3">To get started</h3>
      <p className="guide__description">You need to have already installed the Amplify Loyalty
        app from the Shopify app store, <a href="/sign-up">created your account</a>, 
        and <a href="/guides/getting-started">set up your base program</a>.
      </p>

      <h2 className="guide__h2">Editing your theme</h2>
      <div className="guide__mini-section">
        <p className="guide__description">
          From within your Shopify admin, click on <strong>Online Store &gt; Themes. </strong>
          Click on the 3 dots on your active theme next to the <strong>Customize</strong> button and 
          click <strong>Edit code</strong>.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Edit+code.png" alt="Edit code" />
        <p className="guide__description">
          Under the <strong>templates</strong> folder, click on <strong>+ Add a new template</strong>.
          Choose the "page" template type, JSON, and name the file <strong>amplify-loyalty-program </strong>
          and click <strong>Done</strong>.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Create+template.png" alt="Create template" />
        <p className="guide__description">
          Click exit icon in the top left and click on <strong>Online Store 
          &gt; Pages</strong> and <strong>Add page</strong> to create a new page. In the
          newly created page settings, make the title something like "Rewards", set the Visibility 
          to <strong>Visible</strong> and select the newly created Template: <strong>
          amplify-loyalty-program</strong>.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Create+page.png" alt="Create page" />
        <p className="guide__description">
          Go back to <strong>Online Store &gt; Themes</strong> and click on the 3 dots 
          on your live theme and click <strong>Duplicate</strong>. Once the live theme is done 
          duplicating, rename it to something like "Amplify Loyalty" and then click 
          <strong> Customize</strong>.
        </p>
        <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Customize+theme.png" alt="Customize theme" />
        
        <h3 className="guide__h3">Dedicated loyalty page</h3>
        <p className="guide__description">
          In the Theme Editor, click on the dropdown on the top menu and select <strong>Pages &gt;
          amplify-loyalty-program</strong>. 
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Template+selection.png" alt="Select template" />
        <p className="guide__description">
          Within the left sidebar, select the <strong>App embeds</strong> icon and set 
          the Amplify Loyalty toggle to <strong>ON</strong>.
        </p>
        <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/App+Embed.png" alt="App embed" />
      </div>
      <div className="guide__mini-section">
        <h3 className="guide__h3">Shopify sections</h3>
        <p className="guide__description">
          Go back to <strong>Sections</strong> in the left sidebar to continue editing the Rewards page.
          Under <strong>Template</strong>, hide the <strong>Page</strong> section. Click <strong>Add section</strong>,
          <strong> Apps</strong>, and add Amplify Loyalty sections in the folowing order.
          <ol>
            <li style={{fontWeight: 'bold'}}>Hero</li>
            <li style={{fontWeight: 'bold'}}>How it Works</li>
            <li style={{fontWeight: 'bold'}}>Ways to Earn</li>
            <li style={{fontWeight: 'bold'}}>VIP <span style={{fontWeight: 'normal'}}>(only if VIP program is created and active)</span></li>
            <li style={{fontWeight: 'bold'}}>FAQ</li>
            <li style={{fontWeight: 'bold'}}>Call to Action</li>
          </ol>
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/App+sections.png" alt="App sections" />
          <div className="guide__note">
            <div className="guide__note__tooltip">
              <div className="tooltip__icon guide__note__tooltip-icon">?</div>
              <div className="guide__note__tooltip-description">Note</div>
            </div>
            <p className="guide__note__description">
              Some of these sections are dynamic and will change when the customer is logged in. To see
              an example of what the customer sees, click on the <strong>Toggle view</strong> button 
              when editing the section and when available.
            </p>
            <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Toggle+view.png" alt="Toggle view" />
          </div>
          <p className="guide__description">
            These sections are highly customizable to be able to fit your brand! Click 
            on an individual section to change things like text, colors, links and more.
            Every section is set up by default so that you don't have to change a thing if 
            you don't want to. Simply add the sections as shown in this guide and you're done.
          </p>
        </div>

        <div className="guide__mini-section">
          <h3 className="guide__h3">Announcement bar</h3>
          <p className="guide__description">
            Click on the <strong>App embeds</strong> icon in the left sidebar and toggle on 
            the <strong>Announcement bar</strong>. You should now see a bar at the top of your 
            website that you can use to prompt website visitors to join your program. If you want
            to link it to your newly created loyalty page, make sure to update the <strong>URL </strong>
            section in the settings.
          </p>
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Announcement+bar.png" alt="Announcement bar" />

        </div>

        <div className="guide__mini-section">
          <h3 className="guide__h3">Product page</h3>
          <p className="guide__description">
            Optionally you can set up a few sections to engage customers with details 
            about your loyalty program across your website. Click on the template dropdown 
            at the top of the theme editor and click on <strong>Products &gt; Default 
            product</strong>. 
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Product+template.png" alt="Product template" />
          <p className="guide__description">
            Under the <strong>Template &gt; Product information</strong> section (this may 
            differ based on your theme), create a new block and select <strong>Product points</strong>. 
          </p>
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Product+points.png" alt="Product points" />
          <p className="guide__description">
            This section will dynamically show how many points the customer will earn by 
            purchasing this item based on the amount you set up in your program and
            the price of the product. This is meant to be an extra incentive to show the customer 
            on the product page and encourage adds to cart.
          </p>
        </div>
        <div className="guide__mini-section">
          <h3 className="guide__h3">Cart page</h3>
          <p className="guide__description">
            To show customers the total amount of points they will earn and enable them
            to apply coupons they have already redeemed at checkout, click on the template 
            dropdown at the top of the theme editor and click on <strong>Cart</strong>.
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Cart+template.png" alt="Cart template" />
          <p className="guide__description">
            Create a new section at the top of the page and select <strong>Apps &gt; Cart Banner</strong>.
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Cart+banner+block.png" alt="Cart banner block" />
          <p className="guide__description">
            The banner shown will be an example of what a customer will see when they go to the cart 
            page and have something in their cart. It will automatically show the number of points 
            they will earn on their purcahse based on the price of their cart.
          </p>
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Cart+banner.png" alt="Cart banner" />
          <p className="guide__description">
            Next we will add a section for customers to apply coupons to their cart that they
            have available to use (already redeemed for points, received in a giveaway, etc.).
          </p>
          <img className="guide__img guide__img--rnd" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Cart+coupons+block.png" alt="Cart coupons block" />
          <div className="guide__note">
            <div className="guide__note__tooltip">
              <div className="tooltip__icon guide__note__tooltip-icon">?</div>
              <div className="guide__note__tooltip-description">Note</div>
            </div>
            <p className="guide__note__description">
              If you have the default Dawn theme, it is likely that you will not see this section 
              if you add it to the location shown in the example. This is because that section only shows
              when a customer has something in their cart. If this is the case, <strong>from within the 
              theme editor</strong>, go and add something to cart and then return to this page.
            </p>
          </div>
          <p className="guide__description">
            Now you will be able to view example coupons as customers may see them and adjust 
            the styling of your section. By default, this section will refresh the cart page 
            with the coupon applied. If you wish, you can send them directly to checkout with 
            the coupon applied to skip a step.
          </p>
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Cart+coupons.png" alt="Cart coupons" />
          <p className="guide__description">
            Make sure to hit save in the upper right corner when you are satisfied with the settings.
          </p>
        </div>
        
        <h2 className="guide__h2">Conclusion</h2>
        <p className="guide__description">
          Once you have set up every section you want in your loyalty program, be sure to save and publish
          the theme to your live theme so your customers can start interacting with it.
        </p>
        <div className="guide__mini-section">
          <img className="guide__img guide__img--rnd guide__img--lrg" src="https://amplify-loyalty-website.s3.us-east-1.amazonaws.com/Save+%26+publish+theme.png" alt="Save & publish theme" />
          <p className="guide__description">
            Your program is now ready to go! If you need any help setting up your program, have 
            requests for features or anything at all, please email us at 
            <a href="mailto:trevor@amplifyloyalty.com">trevor@amplifyloyalty.com</a>.
          </p>
        </div>




    </div>
  )
}